/**
 * useLogoCrop — the "pick → crop → store" handshake shared by every logo
 * upload surface (Settings → Workspace, the onboarding wizard and the
 * "+ Add New Workspace" modal).
 *
 * `pickLogo(file)` validates the MIME type up front (an unsupported file
 * never opens the dialog) and stages the file for `LogoCropModal`.  The
 * modal then reports back through `saveCrop(dataUrl)` / `failCrop(err)` /
 * `cancelCrop()`, all of which clear the staged file so the dialog closes.
 *
 * Usage:
 *   const logoCrop = useLogoCrop({ onLogo: saveLogo, onError: showError });
 *   <AvatarUpload onFileSelected={logoCrop.pickLogo} />
 *   <LogoCropModal {...logoCrop.modalProps} cropLogo={cropLogo} />
 */
import { useCallback, useMemo, useState } from 'react';

import { logoFileError } from '../utils/logoImage';

export default function useLogoCrop({ onLogo, onError } = {}) {
    const [cropFile, setCropFile] = useState(null);

    const pickLogo = useCallback(
        (file) => {
            if (!file) return;
            const typeError = logoFileError(file);
            if (typeError) {
                onError?.(typeError);
                return;
            }
            setCropFile(file);
        },
        [onError],
    );

    const cancelCrop = useCallback(() => setCropFile(null), []);

    const saveCrop = useCallback(
        (dataUrl) => {
            setCropFile(null);
            onLogo?.(dataUrl);
        },
        [onLogo],
    );

    const failCrop = useCallback(
        (err) => {
            setCropFile(null);
            onError?.(err);
        },
        [onError],
    );

    const modalProps = useMemo(
        () => ({ file: cropFile, onCancel: cancelCrop, onSave: saveCrop, onError: failCrop }),
        [cancelCrop, cropFile, failCrop, saveCrop],
    );

    return { cropFile, pickLogo, cancelCrop, saveCrop, failCrop, modalProps };
}
